import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getProcess } from '../../transactions/transaction';
import { transitionPrivileged } from '../../util/api';
import { isPrivileged } from '../../transactions/transactionProcessPurchase';

const processName = 'default-purchase';
const transitions = getProcess(processName)?.transitions;

const TRANSITION_ACCEPT_OFFER = transitions.TRANSITION_ACCEPT_OFFER;
const TRANSITION_SEND_OFFER = transitions.TRANSITION_SEND_OFFER;
const TRANSITION_DECLINE_OFFER = transitions.TRANSITION_DECLINE_OFFER;
const TRANSITION_COUNTER_OFFER = transitions.TRANSITION_COUNTER_OFFER;
const TRANSITION_ACCEPT_AFTER_COUNTER_OFFER = transitions.TRANSITION_ACCEPT_AFTER_COUNTER_OFFER;
const TRANSITION_DECLINE_AFTER_COUNTER_OFFER = transitions.TRANSITION_DECLINE_AFTER_COUNTER_OFFER;

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

const INBOX_PAGE_SIZE = 50;

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/OffersPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/OffersPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/OffersPage/FETCH_ORDERS_OR_SALES_ERROR';

export const UPDATE_OFFER_PROGRESS = 'app/OffersPage/UPDATE_OFFER_PROGRESS';
export const UPDATE_OFFER_SUCCESS = 'app/OffersPage/UPDATE_OFFER_SUCCESS';
export const UPDATE_OFFER_ERROR = 'app/OffersPage/UPDATE_OFFER_ERROR';
export const CLEAR_UPDATE_OFFER_ERROR = 'app/OffersPage/CLEAR_UPDATE_OFFER_ERROR';

export const apiQueryParams = {
  lastTransitions: [
    TRANSITION_SEND_OFFER,
    TRANSITION_ACCEPT_OFFER,
    TRANSITION_DECLINE_OFFER,
    TRANSITION_COUNTER_OFFER,
    TRANSITION_ACCEPT_AFTER_COUNTER_OFFER,
    TRANSITION_DECLINE_AFTER_COUNTER_OFFER,
    // v76: a buyer's own counter, and an accepted offer the buyer started paying.
    'transition/customer-counter-offer',
    'transition/request-payment-after-offer',
  ],
  include: [
    'listing',
    'listing.images',
    // Checkout needs the listing's author and stock to pay an accepted offer.
    'listing.author',
    'listing.currentStock',
    'provider',
    'provider.profileImage',
    'customer',
    'customer.profileImage',
    'booking',
  ],
  'fields.transaction': [
    'createdAt',
    // Checkout reads the process from an accepted offer's order.
    'processName',
    'processVersion',
    'state',
    'lastTransition',
    'lastTransitionedAt',
    'transitions',
    'payinTotal',
    'payoutTotal',
    'lineItems',
    'protectedData',
    'metadata',
  ],
  'fields.listing': ['title', 'description', 'publicData', 'price', 'images'],
  'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
  ...createImageVariantConfig('listing-card', 400, 1),
  ...createImageVariantConfig('listing-card-2x', 800, 1),
  page: 1,
  per_page: INBOX_PAGE_SIZE,
};

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
  // The latest page of offers asked for: an older answer arriving after it is dropped.
  offersRequest: 0,
  updateOfferInProgress: false,
  updateOfferSuccess: false,
  updateOfferError: null,
};

export default function offersPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPDATE_OFFER_PROGRESS:
      return { ...state, updateOfferInProgress: true, updateOfferError: null };
    case UPDATE_OFFER_SUCCESS:
      return { ...state, updateOfferSuccess: true, updateOfferInProgress: false };
    case UPDATE_OFFER_ERROR:
      return { ...state, updateOfferError: action.payload || true, updateOfferInProgress: false };
    case CLEAR_UPDATE_OFFER_ERROR:
      return { ...state, updateOfferError: null };
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchOrdersOrSalesError: null,
        offersRequest: action.meta?.request ?? state.offersRequest,
      };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      if (action.meta?.request !== state.offersRequest) return state;
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      if (action.meta?.request !== state.offersRequest) return state;
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = request => ({
  type: FETCH_ORDERS_OR_SALES_REQUEST,
  meta: { request },
});
const fetchOrdersOrSalesSuccess = (request, response) => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
  meta: { request },
});
const fetchOrdersOrSalesError = (request, e) => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
  meta: { request },
});

const updateOfferProgress = () => ({
  type: UPDATE_OFFER_PROGRESS,
});

const updateOfferSuccess = () => ({
  type: UPDATE_OFFER_SUCCESS,
});
const updateOfferError = e => ({
  type: UPDATE_OFFER_ERROR,
  payload: e,
  error: true,
});

/** The offer window closes or opens on another offer: an earlier refusal isn't shown there. */
export const clearUpdateOfferError = () => ({ type: CLEAR_UPDATE_OFFER_ERROR });

// ================ Thunks ================ //

/** One page of offers (the server's pagination, as asked for by the URL's page). */
let offersRequests = 0;
const fetchOffers = page => async (dispatch, getState, sdk) => {
  offersRequests += 1;
  const request = offersRequests;
  dispatch(fetchOrdersOrSalesRequest(request));
  try {
    const response = await sdk.transactions.query({ ...apiQueryParams, page });
    dispatch(addMarketplaceEntities(response));
    dispatch(fetchOrdersOrSalesSuccess(request, response));
    return response;
  } catch (e) {
    dispatch(fetchOrdersOrSalesError(request, storableError(e)));
    throw e;
  }
};

/**
 * Answers an offer. Accepting and countering are the server's (process v76:
 * it writes the price and holds the item), through /api/transition-privileged;
 * declining is the plain transition. A refusal is rethrown, so the page
 * doesn't carry on as if it had worked.
 */
export const updateOffer = ({ id, transition, offerParams = null }) => async (dispatch, getState, sdk) => {
  dispatch(updateOfferProgress());
  try {
    if (isPrivileged(transition)) {
      const offerPrice = offerParams?.type === 'counter' ? offerParams.offerPrice : undefined;
      await transitionPrivileged({
        bodyParams: { id, transition, params: offerPrice != null ? { offerPrice } : {} },
        queryParams: { expand: true },
      });
    } else {
      await sdk.transactions.transition({ id, transition, params: {} }, { expand: true });
    }

    // The page the seller is on, read again
    const page = getState().OffersPage?.pagination?.page || 1;
    await fetchOffers(page)(dispatch, getState, sdk);
    await dispatch(updateOfferSuccess());
    return id;
  } catch (e) {
    dispatch(updateOfferError(storableError(e)));
    throw e;
  }
};

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const { page = 1 } = parse(search);
  return fetchOffers(page)(dispatch, getState, sdk);
};
